import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Footer } from './Footer';

const navItems = [
  { key: 'home', path: '/' },
  { key: 'me-info', path: '/me-info' },
  { key: 'mitmachen', path: '/mitmachen' },
  { key: 'timeline', path: '/timeline' },
  { key: 'about', path: '/about' },
  { key: 'donate', path: '/donate' },
];

const getNavText = (key: string, language: 'en' | 'de'): string => {
  const translations: Record<string, Record<'en' | 'de', string>> = {
    'home': { en: 'Home', de: 'Startseite' },
    'me-info': { en: 'About M.E.', de: 'Über M.E.' },
    'mitmachen': { en: 'Get Involved', de: 'Mitmachen' },
    'timeline': { en: 'Runs', de: 'Läufe' },
    'about': { en: 'About Us', de: 'Über uns' },
    'donate': { en: 'Donate', de: 'Spenden' },
  };
  return translations[key]?.[language] || key;
};

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { language } = useLanguage();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ p: theme.spacing(1) }}>
        <Box
          component="img"
          src="/logo.png"
          alt="Run for M.E."
          sx={{
            height: '75px',
            width: '75px',
            borderRadius: '50%',
            objectFit: 'contain',
            objectPosition: 'center',
            backgroundColor: 'white',
            transform: 'scale(1.3)',
          }}
        />
      </Box>
      <List>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.key} component={Link} to={item.path}>
              <ListItemText
                primary={getNavText(item.key, language)}
                sx={{
                  color: isActive ? 'primary.main' : 'text.primary',
                  fontWeight: isActive ? 600 : 400,
                  '& .MuiListItemText-primary': {
                    color: isActive ? 'primary.main' : 'text.primary',
                  },
                }}
              />
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ p: theme.spacing(2) }}>
        <LanguageSwitcher mobile />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: 'white',
          color: 'text.primary',
          borderRadius: 0,
          border: 'none',
          borderBottom: 'none',
          // Add mobile-specific top positioning to account for mobile browser UI
          top: { xs: 'env(safe-area-inset-top)', md: 0 },
        }}
      >
        <Toolbar sx={{
          padding: { xs: theme.spacing(0, 2), md: theme.spacing(0, 2, 0, 0) },
          margin: 0,
          minHeight: 'auto'
        }}>
          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          ) : null}

          {!isMobile && (
            <Box
              component={Link}
              to="/"
              sx={{
                flexGrow: 1,
                textDecoration: 'none',
                color: 'inherit',
                display: 'flex',
                alignItems: 'center',
                ml: 2,
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="Run for M.E."
                sx={{
                  height: '65px',
                  width: '65px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  backgroundColor: 'white',
                }}
              />
            </Box>
          )}

          {isMobile ? (
            <Box
              component={Link}
              to="/"
              sx={{
                flexGrow: 1,
                textDecoration: 'none',
                color: 'inherit',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="Run for M.E."
                sx={{
                  height: '65px',
                  width: '65px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  backgroundColor: 'white',
                }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {navItems.map((item) => (
                <Typography
                  key={item.key}
                  component={Link}
                  to={item.path}
                  sx={{
                    color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {getNavText(item.key, language)}
                </Typography>
              ))}
              <LanguageSwitcher />
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        anchor="left"
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>

      <Box component="main" sx={{
        flexGrow: 1,
        margin: 0,
        padding: 0,
        pt: {
          xs: 'calc(70px + env(safe-area-inset-top))',
          md: '80px'
        }
      }}>
        {children}
      </Box>
      <Footer />
    </Box>
  );
};
